import { Fragment, useEffect, useRef, useState } from 'react'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { createPortal } from 'react-dom'
import { trackInternalEvent } from '@rio/tracking'
import { useDispatch } from 'react-redux'
import { Colors } from 'cio-design-library/design-tokens'
import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { actions as virtualCoachActions } from 'builder/modules/virtualCoach/virtualCoachModule'
import Markdown from './Markdown'
import { Icon } from './assets'

import {
  Avatar,
  ChatBotBody,
  ChatBotForm,
  ChatBotFooter,
  ChatBotHeader,
  FlexBox,
  Message,
  SubmitButton,
  IconWrapper,
  ChatBotContainer,
  ScrollIndicator,
  Title,
  Trigger,
  ErrorMessage,
  LoadingDots,
  AssistantLink,
} from './styles'
import { useChatScroll, useLayoutVariant, useVirtualCoach } from './hooks'
import { LayoutVariant, TriggerVariant } from './types'
import AutoExpandingPromptInput from './PromptInput'

type Props = {
  layoutVariant: LayoutVariant
  triggerVariant: TriggerVariant
}

export function VirtualCoach(props: Props) {
  const { i18n } = useI18n()
  const { isPhone } = useMediaQueries()
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const { messages, error, loading, handleSubmit } = useVirtualCoach()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [isTyping, setIsTyping] = useState(false)

  const { layoutVariant, toggleFullScreen } = useLayoutVariant(props.layoutVariant)
  const { triggerVariant } = props

  const scrollUtils = useChatScroll({ open: isOpen, messages: messages })
  const { chatBodyRef, trackScroll, showScrollIndicator, scrollToBottom } = scrollUtils

  const toggleOpenState = () => {
    setIsOpen(prev => {
      const isNowOpen = !prev

      if (isNowOpen) {
        dispatch(virtualCoachActions.openChatBot())
        trackInternalEvent('chat_open')
      } else {
        trackInternalEvent('chat_closed')
      }

      return isNowOpen
    })
  }

  useEffect(() => {
    /**
     * Make sure the body is scrollable when the chatbot is open in fullscreen mode.
     * This is necessary because the chatbot is a modal that covers the entire screen.
     * Also toogle it back to normal when the chatbot is closed.
     */
    if (isOpen && layoutVariant === 'fullscreen') {
      disableBodyScroll(document.body)
    } else {
      enableBodyScroll(document.body)
    }
  }, [isOpen, layoutVariant])

  useEffect(() => {
    if (inputRef.current && !loading && isOpen) {
      inputRef.current.focus()
    }
  }, [isOpen, loading])

  return (
    <Fragment>
      {isOpen ? (
        createPortal(
          <ChatBotContainer className={layoutVariant}>
            <ChatBotHeader>
              <FlexBox gap="10px" justifyContent="flex-start">
                <Icon.AiCoach />
                <Title>{i18n.t('builder.virtual_coach.title')}</Title>
              </FlexBox>
              <FlexBox>
                <IconWrapper
                  as="button"
                  type="button"
                  fill={Colors.Neutral100}
                  onClick={toggleFullScreen}
                  hidden={isPhone}
                >
                  <Icon.Expand />
                </IconWrapper>

                <IconWrapper
                  as="button"
                  type="button"
                  fill={Colors.Neutral100}
                  onClick={toggleOpenState}
                >
                  <Icon.Close />
                </IconWrapper>
              </FlexBox>
            </ChatBotHeader>
            <ChatBotForm onSubmit={handleSubmit}>
              <ChatBotBody ref={chatBodyRef} onScroll={trackScroll}>
                {messages?.map((message, index) => (
                  <Message key={index}>
                    <Avatar visibility={message.role === 'assistant' ? 'visible' : 'hidden'}>
                      <Icon.Assistant width={24} height={24} />
                    </Avatar>

                    {message.role === 'assistant' && (
                      <FlexBox gap="10px" direction="column" alignItems="flex-start">
                        <div className="assistant">
                          <Markdown>{message.content}</Markdown>
                        </div>

                        {message.urlLinks?.map((link, index) => (
                          <AssistantLink
                            key={index}
                            href={link.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => {
                              trackInternalEvent('chat_click_link', {
                                name: link.name,
                                url: link.url,
                              })
                            }}
                          >
                            {link.name}
                            <Icon.LinkArrow />
                          </AssistantLink>
                        ))}
                      </FlexBox>
                    )}

                    {message.role === 'user' && <div className="user"> {message.content}</div>}
                  </Message>
                ))}

                {error && (
                  <ErrorMessage>
                    <Icon.CircleError /> {i18n.t(error)}
                  </ErrorMessage>
                )}

                {loading && (
                  <FlexBox gap="26px" alignItems="center">
                    <Avatar>
                      <Icon.Assistant width={24} height={24} />
                    </Avatar>
                    <LoadingDots />
                  </FlexBox>
                )}

                {showScrollIndicator && (
                  <ScrollIndicator type="button" onClick={scrollToBottom}>
                    <IconWrapper>
                      <Icon.ArrowDown />
                    </IconWrapper>
                  </ScrollIndicator>
                )}
              </ChatBotBody>

              <ChatBotFooter>
                <div style={{ position: 'relative' }}>
                  <AutoExpandingPromptInput
                    name="prompt"
                    autoComplete="off"
                    autoFocus={true}
                    placeholder={i18n.t('builder.virtual_coach.input_placeholder')}
                    required
                    ref={inputRef}
                    disabled={loading}
                    onChange={e => setIsTyping(e.target.value.trim() !== '')}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        e.currentTarget.form?.requestSubmit()
                        setIsTyping(false)
                      }
                    }}
                  />

                  <SubmitButton type="submit" disabled={loading} isTyping={isTyping}>
                    <IconWrapper
                      stroke={isTyping ? Colors.Blue50 : Colors.Neutral50}
                      fill={isTyping ? Colors.Blue50 : Colors.White}
                    >
                      <Icon.Send />
                    </IconWrapper>
                  </SubmitButton>
                </div>
              </ChatBotFooter>
            </ChatBotForm>
          </ChatBotContainer>,
          document.body,
        )
      ) : (
        <Trigger className={triggerVariant} onClick={toggleOpenState}>
          <FlexBox gap="15px" justifyContent="flex-start">
            <Icon.Assistant />
            {triggerVariant === 'default' && (
              <span>{i18n.t('builder.virtual_coach.trigger_label')}</span>
            )}
          </FlexBox>
        </Trigger>
      )}
    </Fragment>
  )
}

export function MobileVirtualCoach(props: Partial<Props>) {
  const { isPhone } = useMediaQueries()
  if (!isPhone) return null

  const defaultProps = {
    layoutVariant: props.layoutVariant ?? 'fullscreen',
    triggerVariant: props.triggerVariant ?? 'navitem',
  }

  return <VirtualCoach {...defaultProps} />
}

export function TabletAndDesktopVirtualCoach(props: Props) {
  const { isPhone } = useMediaQueries()
  if (isPhone) return null

  return <VirtualCoach {...props} />
}

export function DesktopVirtualCoach(props: Props) {
  const { isDesktop } = useMediaQueries()
  if (!isDesktop) return null

  return <VirtualCoach {...props} />
}
